import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { V2_BASE } from '../config';
import {
  Home, ChevronRight, ShieldCheck, Search, SlidersHorizontal, Download,
  LayoutGrid, MoreVertical, Plus,
} from 'lucide-react';
import { EMPTY_ILLU } from '../illu';
import './contabilita.css';
import './hosting.css';

const api = axios.create({ withCredentials: true });
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const STATE_TAG = (raw) => {
  const s = (raw || '').toLowerCase();
  if (['attivo', 'attiva', 'active', 'ok', 'valido', 'firmato'].includes(s)) return { cls: 'ok', label: 'Attiva' };
  if (['scaduto', 'scaduta', 'sospeso', 'fallita', 'fallito', 'errore'].includes(s)) return { cls: 'danger', label: raw };
  // La casella esiste ma non spedisce finché il titolare non firma: va detto
  // con parole sue, non con il nome tecnico dello stato.
  if (['da_firmare', 'da firmare'].includes(s)) return { cls: 'warn', label: 'Da firmare' };
  if (['in_attesa_verifica'].includes(s)) return { cls: 'warn', label: 'In verifica' };
  if (['in_attesa', 'in attesa', 'pending', 'in_lavorazione'].includes(s)) return { cls: 'warn', label: 'In attesa' };
  return { cls: 'info', label: raw || '—' };
};

const NavIcon = ({ name, size = 24 }) => (
  <img className="nfx-nav-ico" src={`/nav/${name}.svg`} alt="" aria-hidden="true" width={size} height={size} />
);

const TABS = [
  { key: 'posta',   label: 'Posta Certificata',        path: '/pec-trust/posta-certificata' },
  { key: 'spid',    label: 'SPID',                     path: '/pec-trust/spid' },
  { key: 'firma',   label: 'Firma Digitale',           path: '/pec-trust/firma-digitale' },
  { key: 'fattura', label: 'Fatturazione Elettronica', path: '/pec-trust/fatturazione-elettronica' },
  { key: 'marca',   label: 'Marca Temporale',          path: '/pec-trust/marca-temporale' },
];

const IlluEmpty = ({ title, desc, primary, link, onPrimary, onLink }) => (
  <div className="nfx-card">
    <div className="nfx-hd-illu">
      <div className="nfx-hd-illu__l">
        <div className="nfx-hd-illu__title">{title}</div>
        <div className="nfx-hd-illu__desc">{desc}</div>
        <div className="nfx-hd-illu__actions">
          <button className="nfx-btn-blue" onClick={onPrimary}>{primary}</button>
          {link && <button className="nfx-hd-illu__link" onClick={onLink}>{link}</button>}
        </div>
      </div>
      <div className="nfx-hd-illu__illu"><img src={EMPTY_ILLU} alt="" /></div>
    </div>
  </div>
);

const PecDashboard = ({ tab }) => {
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const offer = () => window.open('https://neoflux.it', '_blank');
  const [caselle, setCaselle] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true; setLoading(true);
    api.get('/api/pec/caselle')
      .then(r => { if (alive) setCaselle(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setCaselle([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  /* Caselle create ma ferme in attesa della firma del titolare. */
  const daFirmare = caselle.filter(c => c.contractUrl
    && (c.stato_contratto || '').toLowerCase() === 'da_firmare');

  return (
    <div>
      {/* Breadcrumb */}
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => go('')} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur"><ShieldCheck size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: '#1d3c51' }} />PEC &amp; Trust Services</span>
      </div>
      <h1 className="nfx-cb-title">Dashboard PEC &amp; Trust Services</h1>

      {/* Tabs */}
      <div className="nfx-hdtabs">
        {TABS.map(t => (
          <button key={t.key} className={`nfx-hdtab${tab === t.key ? ' is-active' : ''}`} onClick={() => go(t.path)}>{t.label}</button>
        ))}
      </div>

      {/* ── Posta Certificata ── */}
      {tab === 'posta' && (
        loading ? <div className="nfx-card"><div className="nfx-loading">Caricamento…</div></div>
          : caselle.length === 0 ? (
            <IlluEmpty title="Non hai nessuna casella PEC"
              desc="Attiva una casella di Posta Elettronica Certificata con valore legale per le tue comunicazioni ufficiali."
              primary="Attiva una PEC" link="Scopri le nostre offerte" onPrimary={offer} onLink={offer} />
          ) : (
            <div className="nfx-card">
              {/*
                * Il contratto da firmare: finché non è firmato la casella è
                * creata ma non spedisce, e il cliente non ha modo di saperlo.
                * Il link di firma (eSAW) arriva anche per email, ma le email
                * si perdono: qui è sempre a portata di mano.
                */}
              {daFirmare.map(c => (
                <div key={`firma-${c.id}`} className="nfx-pec-firma">
                  <div className="nfx-pec-firma__txt">
                    <b>{c.indirizzo_pec}</b> è stata creata ma non è ancora attiva:
                    manca la firma del contratto. Firmandolo online, in pochi minuti, la casella entra in funzione.
                  </div>
                  <a className="nfx-btn-blue nfx-pec-firma__btn" href={c.contractUrl}
                     target="_blank" rel="noreferrer">Firma il contratto</a>
                </div>
              ))}
              <div className="nfx-tb">
                <span className="nfx-tb__count"><b>{caselle.length}</b> {caselle.length === 1 ? 'Casella PEC' : 'Caselle PEC'}</span>
                <button className="nfx-tb__btn" onClick={offer}><Plus size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Attiva una PEC</button>
                <span className="nfx-tb__spacer" />
                <div className="nfx-tb__actions">
                  <button className="nfx-iconbtn"><Search size={18} /></button>
                  <button className="nfx-iconbtn"><SlidersHorizontal size={18} /></button>
                  <button className="nfx-iconbtn"><Download size={18} /></button>
                  <button className="nfx-iconbtn is-active"><LayoutGrid size={18} /></button>
                </div>
              </div>
              <table className="nfx-table">
                <thead><tr><th className="nfx-td-ico" /><th>Indirizzo PEC</th><th>Dominio</th><th>Piano</th><th>Stato</th><th>Scadenza</th><th className="nfx-td-actions" /></tr></thead>
                <tbody>
                  {caselle.map((c, i) => {
                    const st = STATE_TAG(c.stato_contratto || c.stato || c.tipo);
                    return (
                      <tr key={c.id || i}>
                        <td className="nfx-td-ico"><NavIcon name="nav-pec" size={26} /></td>
                        <td className="nfx-cell-strong">{c.indirizzo_pec || c.nome || '—'}</td>
                        <td>{c.dominio || '—'}</td>
                        <td>{c.piano || '—'}</td>
                        <td><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></td>
                        <td>{fmtDate(c.data_scadenza)}</td>
                        <td className="nfx-td-actions">
                          {c.contractUrl && (c.stato_contratto || '').toLowerCase() === 'da_firmare'
                            ? <a className="nfx-pec-firma__link" href={c.contractUrl} target="_blank" rel="noreferrer">Firma</a>
                            : <button className="nfx-iconbtn"><MoreVertical size={16} /></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* ── SPID ── */}
      {tab === 'spid' && (
        <IlluEmpty title="Non hai nessuna identità SPID"
          desc="Richiedi la tua identità digitale SPID per accedere ai servizi della Pubblica Amministrazione e dei privati."
          primary="Richiedi lo SPID" link="Scopri di più" onPrimary={offer} onLink={offer} />
      )}

      {/* ── Firma Digitale ── */}
      {tab === 'firma' && (
        <IlluEmpty title="Non hai nessuna firma digitale"
          desc="Firma documenti con valore legale grazie alla firma elettronica qualificata eIDAS, valida in tutta Europa."
          primary="Attiva la Firma Digitale" link="Scopri di più" onPrimary={offer} onLink={offer} />
      )}

      {/* ── Fatturazione Elettronica ── */}
      {tab === 'fattura' && (
        <IlluEmpty title="Nessun servizio di fatturazione elettronica"
          desc="Gestisci l'invio e la conservazione delle fatture elettroniche verso PA e privati (SDI)."
          primary="Attiva la Fatturazione" link="Scopri di più" onPrimary={offer} onLink={offer} />
      )}

      {/* ── Marca Temporale ── */}
      {tab === 'marca' && (
        <IlluEmpty title="Nessuna marca temporale"
          desc="Certifica la data e l'ora di un documento con valore legale grazie alla marcatura temporale."
          primary="Acquista Marche Temporali" link="Scopri di più" onPrimary={offer} onLink={offer} />
      )}
    </div>
  );
};

export const PostaCertificataPage = () => <PecDashboard tab="posta" />;
export const SpidPage = () => <PecDashboard tab="spid" />;
export const FirmaDigitalePage = () => <PecDashboard tab="firma" />;
export const FatturazioneElettronicaPage = () => <PecDashboard tab="fattura" />;
export const MarcaTemporalePage = () => <PecDashboard tab="marca" />;
